import {
	useEmailTemplatesList,
	useUpdateEmailTemplate,
} from '@dealstack/frontend/shared/components/EmailTemplates/hooks/useEmailTemplates';
import {
	isDealTemplateScope,
	type EmailTemplateScope,
} from '@dealstack/frontend/shared/components/EmailTemplates/EmailTemplates.types';
import { CardTableViewSelector } from '@dealstack/frontend/shared/components/CardTableViewSelector/CardTableViewSelector';
import { usePersistedCardTableView } from '@dealstack/frontend/shared/components/CardTableViewSelector/usePersistedCardTableView';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	Alert,
	Button,
	Center,
	Group,
	SimpleGrid,
	Skeleton,
	Stack,
	Text,
	TextInput,
	Title,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { useEmailTemplateAccess } from '../hooks/useEmailTemplateAccess';
import { useTemplateCategoryLabel } from '../hooks/useTemplateCategoryLabel';
import { useTemplateSummaryLabels } from '../hooks/useTemplateSummaryLabels';
import { groupTemplatesByCategory } from '../utils/templateCategories';
import { EmailTemplateCard } from './EmailTemplateCard';
import { EmailTemplateTable } from './EmailTemplateTable';

interface Props {
	scope: EmailTemplateScope;
	onEdit: (templateType: string) => void;
}

const PLACEHOLDER_ROWS = ['first', 'second', 'third', 'fourth'];

// Clears the theme's label-clearance margins, which a `styles` prop cannot beat,
// so the field lines up with the view switch beside it.
const SearchField = styled.div`
	.mantine-InputWrapper-root {
		margin: 0;
	}

	.mantine-Input-input {
		height: 2.5rem;
		min-height: 2.5rem;
		margin-top: 0;
	}
`;

export const EmailTemplateList = ({ scope, onEdit }: Props) => {
	const tMipManage = useTranslationMipManage();
	const { data: templates = [], isLoading, isError, refetch } = useEmailTemplatesList({ scope });
	const [search, setSearch] = useState('');
	const [view, setView] = usePersistedCardTableView(
		isDealTemplateScope(scope.kind)
			? StorageKeys.DEAL_EMAIL_TEMPLATES_VIEW
			: StorageKeys.MIP_PROGRAMME_EMAIL_TEMPLATES_VIEW,
		'cards'
	);
	const categoryLabel = useTemplateCategoryLabel();
	const { versionLabel, statusLabel } = useTemplateSummaryLabels();
	const {
		mutate: mutateUpdate,
		variables: pendingUpdate,
		isPending: isToggling,
	} = useUpdateEmailTemplate({ scope });
	const { canEdit } = useEmailTemplateAccess(scope);

	const handleToggle = (templateType: string, enabled: boolean) =>
		mutateUpdate({
			type: templateType,
			body: { enabled },
			successMessage: enabled
				? tMipManage('email_templates.toast.activated', {
						defaultValue: 'Template activated',
					})
				: tMipManage('email_templates.toast.deactivated', {
						defaultValue: 'Template set to draft',
					}),
		});

	const togglingType = isToggling ? pendingUpdate?.type ?? null : null;

	const groups = useMemo(() => {
		const query = search.trim().toLowerCase();
		const matching = query
			? templates.filter(
					(template) =>
						template.name.toLowerCase().includes(query) ||
						template.description.toLowerCase().includes(query)
				)
			: templates;
		return groupTemplatesByCategory(matching);
	}, [templates, search]);

	if (isError) {
		return (
			<Alert color="red" title={tMipManage('email_templates.list.error.title')}>
				<Button variant="light" onClick={() => refetch()}>
					{tMipManage('email_templates.common.retry')}
				</Button>
			</Alert>
		);
	}

	return (
		<Stack gap="lg">
			<Group justify="space-between" align="flex-start" wrap="nowrap" gap="xl">
				<Stack gap={4}>
					<Title order={4} fz={18} fw={600}>
						{isDealTemplateScope(scope.kind)
							? tMipManage('email_templates.list.deal_title', {
									defaultValue: 'Deal email templates',
								})
							: tMipManage('email_templates.list.title', {
									defaultValue: 'Programme email templates',
								})}
					</Title>
					<Text fz="0.8125rem" lh="1.21875rem" c="gray.9" maw={640}>
						{isDealTemplateScope(scope.kind)
							? tMipManage('email_templates.list.deal_description', {
									defaultValue:
										'Customise the invitations this deal sends. Edits apply only to this deal; unchanged templates follow the Dealstack default.',
								})
							: tMipManage('email_templates.list.description', {
									defaultValue:
										'Customise the transactional emails this programme sends. Edits apply only to this programme; unchanged templates follow the Dealstack default.',
								})}
					</Text>
					{isDealTemplateScope(scope.kind) && (
						<Text fz="0.75rem" lh="1.125rem" c="gray.9" maw={640}>
							{tMipManage('email_templates.list.deal_sso_note', {
								defaultValue:
									'Single sign-on invitations are not covered by these templates: someone whose email domain signs in through SSO receives the Dealstack default.',
							})}
						</Text>
					)}
				</Stack>
				<Group gap="xs" wrap="nowrap">
					<SearchField>
						<TextInput
							placeholder={tMipManage(
								'email_templates.list.search_templates_placeholder',
								{
									defaultValue: 'Search templates...',
								}
							)}
							value={search}
							onChange={(event) => setSearch(event.currentTarget.value)}
							leftSection={<Icon variant="search-v3" size="lg" />}
							w={240}
						/>
					</SearchField>
					<CardTableViewSelector value={view} onViewChange={setView} controlSize={34} />
				</Group>
			</Group>

			{isLoading &&
				(view === 'cards' ? (
					<SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="sm">
						{PLACEHOLDER_ROWS.map((row) => (
							<Skeleton key={row} height="8.125rem" radius="md" />
						))}
					</SimpleGrid>
				) : (
					<Stack gap="xs">
						{PLACEHOLDER_ROWS.map((row) => (
							<Skeleton key={row} height="3rem" radius="sm" />
						))}
					</Stack>
				))}

			{!isLoading && groups.length === 0 && (
				<Center py="xl">
					<Stack gap={4} align="center">
						<Text fw={500} fz="0.8125rem">
							{search.trim()
								? tMipManage('email_templates.list.empty.title', {
										defaultValue: 'No templates found',
									})
								: tMipManage('email_templates.list.empty.none')}
						</Text>
						{search.trim() && (
							<Text fz="0.75rem" c="gray.9">
								{tMipManage('email_templates.list.empty.hint', {
									defaultValue: 'Try a different search term.',
								})}
							</Text>
						)}
					</Stack>
				</Center>
			)}

			{groups.map((group) => (
				<Stack key={group.category} gap="sm">
					<Text fw={600} fz="0.8125rem" lh="1.21875rem">
						{categoryLabel(group.category)}
					</Text>
					{view === 'cards' ? (
						<SimpleGrid cols={{ base: 1, md: 2, xl: 3 }} spacing="sm">
							{group.templates.map((template) => (
								<EmailTemplateCard
									key={template.templateType}
									template={template}
									version={versionLabel(template)}
									statusLabel={statusLabel(template)}
									toggling={togglingType === template.templateType}
									onSelect={() => onEdit(template.templateType)}
									readOnly={!canEdit}
									onToggle={(enabled) =>
										handleToggle(template.templateType, enabled)
									}
								/>
							))}
						</SimpleGrid>
					) : (
						<EmailTemplateTable
							templates={group.templates}
							togglingType={togglingType}
							readOnly={!canEdit}
							onSelect={onEdit}
							onToggle={handleToggle}
						/>
					)}
				</Stack>
			))}
		</Stack>
	);
};
